import { computed, ref } from 'vue'
import { getChatLimits } from '@/utils/chatApi'
import { estimateTokens } from '@/components/chat/mock/chatLimitsMock'

const plan = ref('guest')
const requestsUsed = ref(0)
const requestsTotal = ref(3)
const tokensUsed = ref(0)
const tokensTotal = ref(0)
const allowed = ref(true)
const reason = ref(null)
let limitsLoading = false

function isAuthenticatedNow() {
    try {
        return Boolean(localStorage.getItem('token'))
    } catch (error) {
        return false
    }
}

const isAuthenticated = ref(isAuthenticatedNow())
let pollTimer = null

if (typeof window !== 'undefined') {
    window.addEventListener('storage', () => {
        isAuthenticated.value = isAuthenticatedNow()
    })

    pollTimer = setInterval(() => {
        const next = isAuthenticatedNow()
        if (next !== isAuthenticated.value) {
            isAuthenticated.value = next
        }
    }, 1500)
}

if (typeof window !== 'undefined' && pollTimer) {
    window.addEventListener('beforeunload', () => clearInterval(pollTimer))
}

const accessTier = computed(() => {
    if (!isAuthenticated.value) {
        return 'guest'
    }
    return plan.value === 'pro' ? 'pro' : 'free'
})

const remaining = computed(() => {
    if (accessTier.value === 'pro') {
        return Math.max(0, tokensTotal.value - tokensUsed.value)
    }
    return Math.max(0, requestsTotal.value - requestsUsed.value)
})

const total = computed(() => {
    if (accessTier.value === 'pro') {
        return tokensTotal.value
    }
    return requestsTotal.value
})

const isLimitReached = computed(() => allowed.value === false || remaining.value <= 0)

function refreshAuthState() {
    isAuthenticated.value = isAuthenticatedNow()
    refreshLimits()
}

function consumeRequest() {
    // Источник правды — Laravel. После ответа обновляем лимиты с сервера,
    // локально больше не блокируем гостя по localStorage.
    refreshLimits()
}

function trackTokenUsage(amount) {
    if (!amount || amount <= 0) {
        return
    }
    if (isAuthenticated.value && plan.value === 'pro') {
        tokensUsed.value = Math.min(tokensTotal.value, tokensUsed.value + amount)
    }
}

function resetForTesting() {
    requestsUsed.value = 0
    requestsTotal.value = 3
    allowed.value = true
    reason.value = null
    refreshLimits()
}

async function refreshLimits() {
    if (limitsLoading) return
    limitsLoading = true
    try {
        const limits = await getChatLimits()
        plan.value = limits.plan || plan.value
        requestsUsed.value = Number(limits.requestsUsed || 0)
        requestsTotal.value = Number(limits.requestsTotal || requestsTotal.value || 0)
        tokensUsed.value = Number(limits.tokensUsed || 0)
        tokensTotal.value = Number(limits.tokensTotal || 0)
        allowed.value = limits.allowed !== false
        reason.value = limits.reason || null
    } catch (e) {
        // Если /usage временно недоступен, не блокируем чат на клиенте.
        allowed.value = true
        reason.value = null
    } finally {
        limitsLoading = false
    }
}

export function useChatAccessMock() {
    refreshAuthState()
    refreshLimits()
    return {
        isAuthenticated,
        accessTier,
        remaining,
        total,
        isLimitReached,
        reason,
        plan,
        tokensUsed,
        tokensTotal,
        requestsUsed,
        requestsTotal,
        consumeRequest,
        trackTokenUsage,
        refreshAuthState,
        refreshLimits,
        resetForTesting,
        estimateTokens,
    }
}
